// Auto-generated lightweight class for StakeholderNeed
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { StakeholderNeedReadAPI, StakeholderNeedWriteAPI, StakeholderNeedReadAPISchema, StakeholderNeedWriteAPISchema } from '../types/stakeholderneed';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasAttribute, HasAttributeValue, HasDerivedValue, HasStakeholderNeed, Mentions, NeedHasStakeholder, NeedTransformedToRequirement } from '../../relationships/relationship';

import { Requirement } from './requirement';
import { LogicalPart } from './logicalpart';
import { SecurityScope } from '../../shared/api/securityscope';
import { Attribute } from './attribute';
import { AttributeValue } from './attributevalue';
import { DerivedValue } from './derivedvalue';
import { Expression } from './expression';
import { User } from '../../base/api/user';



export interface StakeholderNeed extends Partial<StakeholderNeedReadAPI> {}
export class StakeholderNeed extends NeoNodeBase<StakeholderNeedReadAPI, StakeholderNeedWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "StakeholderNeed";
  static readonly readSchema = StakeholderNeedReadAPISchema;
  static readonly writeSchema = StakeholderNeedWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    attributes: { target: "Attribute", cardinality: "many", isParent: false, relClass: "HasAttribute", direction: "out" },
    attribute_values: { target: "AttributeValue", cardinality: "many", isParent: false, relClass: "HasAttributeValue", direction: "out" },
    derived_values: { target: "DerivedValue", cardinality: "many", isParent: false, relClass: "HasDerivedValue", direction: "out" },
    mentioned_in_expressions: { target: "Expression", cardinality: "many", isParent: false, relClass: "Mentions", direction: "in" },
    stakeholders: { target: "User", cardinality: "many", isParent: false, relClass: "NeedHasStakeholder", direction: "out" },
    container: { target: "LogicalPart", cardinality: "one", isParent: true, relClass: "HasStakeholderNeed", direction: "in" },
    transformed_to: { target: "Requirement", cardinality: "many", isParent: false, relClass: "NeedTransformedToRequirement", direction: "out" },
  };

  protected get modelName(): string {
    return 'StakeholderNeed';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return StakeholderNeed.label;
  }


  constructor(data: Partial<StakeholderNeedWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: StakeholderNeedReadAPI, loadContext?: Record<string, any>): StakeholderNeed {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { container, transformed_to, security_scope, attributes, attribute_values, derived_values, mentioned_in_expressions, stakeholders, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = StakeholderNeedReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, container: container, transformed_to: transformed_to, security_scope: security_scope, attributes: attributes, attribute_values: attribute_values, derived_values: derived_values, mentioned_in_expressions: mentioned_in_expressions, stakeholders: stakeholders };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return StakeholderNeed.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'container', 'LogicalPart', ModelRegistry);
    loadManyRelationship(processed, 'transformed_to', 'Requirement', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'Attribute', ModelRegistry);
    loadManyRelationship(processed, 'attribute_values', 'AttributeValue', ModelRegistry);
    loadManyRelationship(processed, 'derived_values', 'DerivedValue', ModelRegistry);
    loadManyRelationship(processed, 'mentioned_in_expressions', 'Expression', ModelRegistry);
    loadManyRelationship(processed, 'stakeholders', 'User', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new StakeholderNeed(processed as any);

    return instance;
  }


  // Relationship manager for 'transformed_to' - Python ORM style (cached)
  get transformed_to(): RelationshipManager<this, Requirement> {
    return this.getOrCreateRelationshipManager('transformed_to', () => {
      return new RelationshipManager(this, 'transformed_to', 'many', NeedTransformedToRequirement as typeof Relationship);
    });
  }

  // Relationship manager for 'container' - Python ORM style (cached)
  get container(): RelationshipManager<this, LogicalPart> {
    return this.getOrCreateRelationshipManager('container', () => {
      return new RelationshipManager(this, 'container', 'many', HasStakeholderNeed as typeof Relationship);
    });
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', HasAttribute as typeof Relationship);
    });
  }

  // Relationship manager for 'attribute_values' - Python ORM style (cached)
  get attribute_values(): RelationshipManager<this, AttributeValue> {
    return this.getOrCreateRelationshipManager('attribute_values', () => {
      return new RelationshipManager(this, 'attribute_values', 'many', HasAttributeValue as typeof Relationship);
    });
  }

  // Relationship manager for 'derived_values' - Python ORM style (cached)
  get derived_values(): RelationshipManager<this, DerivedValue> {
    return this.getOrCreateRelationshipManager('derived_values', () => {
      return new RelationshipManager(this, 'derived_values', 'many', HasDerivedValue as typeof Relationship);
    });
  }

  // Relationship manager for 'mentioned_in_expressions' - Python ORM style (cached)
  get mentioned_in_expressions(): RelationshipManager<this, Expression> {
    return this.getOrCreateRelationshipManager('mentioned_in_expressions', () => {
      return new RelationshipManager(this, 'mentioned_in_expressions', 'many', Mentions as typeof Relationship);
    });
  }

  // Relationship manager for 'stakeholders' - Python ORM style (cached)
  get stakeholders(): RelationshipManager<this, User> {
    return this.getOrCreateRelationshipManager('stakeholders', () => {
      return new RelationshipManager(this, 'stakeholders', 'many', NeedHasStakeholder as typeof Relationship);
    });
  }

}
